import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { getEnvironment, isDeployed } from './environment';

// Initialize Bugsnag only in staging and production environments
const apiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;

if (apiKey && apiKey.trim() !== '' && isDeployed()) {
  const environment = getEnvironment();

  // Start Bugsnag error reporting
  Bugsnag.start({
    apiKey,
    plugins: [new BugsnagPluginReact()],
    // Additional configuration options
    releaseStage: environment,
    enabledReleaseStages: ['staging', 'production'],
    // Add app version if available
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
    // Source map configuration for better error tracking
    sendCode: true,
    // Additional configuration
    autoDetectErrors: true,
    autoTrackSessions: true,
    collectUserIp: false,
    maxBreadcrumbs: 50,
    enabledBreadcrumbTypes: [
      'navigation',
      'request',
      'process',
      'log',
      'user',
      'state',
      'error'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] as any
  });

  // Add error callback to filter out expected errors that shouldn't be reported
  Bugsnag.addOnError((event) => {
    const errorClass = event.errors?.[0]?.errorClass;
    const errorMessage = event.errors?.[0]?.errorMessage || '';

    // Filter out NavigationAbortError (user-initiated navigation aborts with unsaved changes)
    if (
      errorClass === 'NavigationAbortError' ||
      errorMessage.includes('Route change aborted by user confirmation dialog')
    ) {
      return false;
    }

    // Filter out AbortError (request cancellations, video play aborts, etc.)
    if (errorClass === 'AbortError' || errorMessage.includes('AbortError')) {
      return false;
    }

    return true;
  });

  // Start Bugsnag performance monitoring
  BugsnagPerformance.start({
    apiKey,
    // Additional performance configuration
    releaseStage: environment,
    enabledReleaseStages: ['staging', 'production']
  });
}

// User identification functions to prevent Bugsnag core.data.userData errors
export const setBugsnagUser = (user: Api.UserObject | null) => {
  if (apiKey && apiKey.trim() !== '' && isDeployed() && Bugsnag) {
    try {
      if (user && user.user_id) {
        const userId = user.user_id.toString();
        const name = `${user.first_name} ${user.last_name}`;

        console.log('Setting Bugsnag user:', { userId, name });

        Bugsnag.setUser(userId, undefined, name);
      } else {
        console.log('Clearing Bugsnag user (logout/clear)');
        // Clear user data when logged out
        Bugsnag.setUser();
      }
    } catch (error) {
      console.warn('Failed to set Bugsnag user:', error);
    }
  } else {
    console.log('Skipping Bugsnag user setup - Bugsnag not initialized');
  }
};

export const clearBugsnagUser = () => {
  if (apiKey && apiKey.trim() !== '' && isDeployed() && Bugsnag) {
    try {
      Bugsnag.setUser();
    } catch (error) {
      console.warn('Failed to clear Bugsnag user:', error);
    }
  }
};

export default Bugsnag;
